import { BookOpenCheck, Bot, Map, RotateCcw, Sparkles } from "lucide-react";
import { getExplorationScore } from "../lib/scoring";
import type { EvidenceId, StoredProgress } from "../types";
import { AssetImage } from "./AssetImage";

interface ReportScreenProps {
  progress: StoredProgress;
  onOpenMap: () => void;
  onRestart: () => void;
}

/** A factual summary of this local journey; it intentionally avoids durable ability labels. */
export function ReportScreen({ progress, onOpenMap, onRestart }: ReportScreenProps) {
  const explorationScore = getExplorationScore(progress);
  const quizFirstScore = progress.quiz?.firstScore ?? progress.quizFirstScore;
  const quizMasteryScore = progress.quiz?.masteryScore ?? progress.quizMasteryScore;
  const evidenceIds = Array.from(new Set(
    Object.values(progress.scenes).flatMap((scene) => [
      ...(scene?.evidenceViews ?? []),
      ...(scene?.selectedEvidenceIds ?? [])
    ])
  )).sort() as EvidenceId[];

  return (
    <main className="report-screen">
      <div className="report-artwork" aria-hidden="true">
        <AssetImage assetId="1A-01" className="report-artwork__desktop" alt="" eager={false} sizes="100vw" />
        <AssetImage assetId="1A-02" className="report-artwork__mobile" alt="" eager={false} sizes="100vw" />
        <AssetImage assetId="6B-01" className="report-artwork__desktop report-artwork__foreground" alt="" sizes="100vw" />
        <AssetImage assetId="6B-02" className="report-artwork__mobile report-artwork__foreground" alt="" sizes="100vw" />
      </div>

      <div className="report-screen__seal"><Sparkles size={21} /> 学习任务已完成</div>
      <span className="section-kicker">本次物候观察档案</span>
      <h1>懂得大自然的语言，从认真观察和核验证据开始</h1>

      <div className="score-band" aria-label="本次学习结果">
        <div><strong>{explorationScore}</strong><span>/100</span><small>探究表现</small></div>
        <div><strong>{quizFirstScore}</strong><span>/5</span><small>测验第一次作答正确</small></div>
        <div><strong>{quizMasteryScore}</strong><span>/5</span><small>订正后正确</small></div>
      </div>

      <div className="evidence-summary" aria-label="本次学习过程">
        <div><BookOpenCheck size={20} /><strong>{progress.metrics.evidenceOpened}</strong><span>次主动查证</span></div>
        <div><RotateCcw size={20} /><strong>{progress.metrics.hintsUsed}</strong><span>次使用提示</span></div>
        <div><Bot size={20} /><strong>{progress.aiAuditCompleted ? "已完成" : "待完成"}</strong><span>AI 草稿审校</span></div>
      </div>

      <div className="report-process">
        <p><strong>已核验证据：</strong>{evidenceIds.length > 0 ? evidenceIds.join("、") : "本次尚无记录"}</p>
        <p>这些数据只描述本次学习旅程，不评价身份、性格、智力或长期能力。答错后的订正同样是有效学习。</p>
      </div>

      <div className="chapter-actions">
        <button className="command-button command-button--secondary" type="button" onClick={onRestart}><RotateCcw size={18} /> 返回封面</button>
        <button className="command-button" type="button" onClick={onOpenMap}><Map size={18} /> 回看旅程</button>
      </div>
    </main>
  );
}
